package se.mittpass.web.pages

import se.mittpass.web.*
import se.mittpass.web.components.*
import se.mittpass.web.pages.*
import se.mittpass.web.viewmodel.*
import se.mittpass.web.style.*


import androidx.compose.runtime.*
import kotlinx.browser.window
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.css.StyleScope
import org.jetbrains.compose.web.dom.*
import se.mittpass.shared.api.CreateSlotRequest
import se.mittpass.shared.api.SlotDto
import se.mittpass.shared.api.UpdateSlotRequest
import se.mittpass.shared.api.UpdateWorkplaceRequest
import se.mittpass.shared.api.WorkplaceDto
import se.mittpass.shared.repository.ClaimsRepository
import se.mittpass.shared.repository.SlotsRepository
import se.mittpass.shared.repository.WorkplacesRepository

@Composable
fun SchemaPage(
    workplace: WorkplaceDto,
    isManager: Boolean,
    currentUserId: Long?,
    slotsRepo: SlotsRepository,
    claimsRepo: ClaimsRepository,
    workplacesRepo: WorkplacesRepository,
    staffRepo: se.mittpass.shared.repository.StaffRepository,
    onWorkplaceUpdated: () -> Unit,
    onNavigateToSemester: (() -> Unit)? = null,
    createAbsenceTrigger: Int = 0
) {
    val schemaScope = rememberCoroutineScope()
    var viewMode by remember { mutableStateOf(ViewMode.WEEK) }
    var anchor by remember { mutableStateOf(todayLocalDate()) }
    var slots by remember { mutableStateOf<List<SlotDto>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var staff by remember { mutableStateOf<List<se.mittpass.shared.api.StaffMemberDto>>(emptyList()) }
    var absences by remember { mutableStateOf<List<se.mittpass.shared.api.AbsenceRequestDto>>(emptyList()) }
    var copyFeedbackMsg by remember { mutableStateOf<String?>(null) }
    var plannerFilter by remember { mutableStateOf(PlannerFilter.ALL) }

    var modalState by remember { mutableStateOf<ModalState?>(null) }
    var selectedAbsence by remember { mutableStateOf<se.mittpass.shared.api.AbsenceRequestDto?>(null) }
    var showSettings by remember { mutableStateOf(false) }
    data class PendingClaim(val slotId: Long, val day: String, val startTime: String, val endTime: String, val isUnclaim: Boolean)
    var confirmPending by remember { mutableStateOf<PendingClaim?>(null) }

    var windowWidth by remember { mutableStateOf(window.innerWidth) }
    val isMobile = windowWidth < 768
    val isNarrow = windowWidth < 1024
    var mobileViewGantt by remember { mutableStateOf(true) } // Default to Gantt view so horizontal scroll and gantt-cell- selectors work

    DisposableEffect(Unit) {
        val handler: (org.w3c.dom.events.Event) -> Unit = { windowWidth = window.innerWidth }
        window.addEventListener("resize", handler)
        onDispose { window.removeEventListener("resize", handler) }
    }

    val (rangeFrom, rangeTo) = remember(viewMode, anchor) {
        when (viewMode) {
            ViewMode.WEEK -> {
                val ws = mondayOf(anchor)
                ws to addDays(ws, 6)
            }
            ViewMode.MONTH -> {
                val first = firstOfMonth(anchor)
                val last = addDays(addMonths(first, 1), -1)
                first to last
            }
            ViewMode.YEAR -> "${anchor.take(4)}-01-01" to "${anchor.take(4)}-12-31"
        }
    }

    val filteredSlots = remember(slots, plannerFilter) {
        when (plannerFilter) {
            PlannerFilter.ALL, PlannerFilter.SHIFTS -> slots
            PlannerFilter.VACATIONS -> emptyList()
        }
    }

    val filteredAbsences = remember(absences, plannerFilter) {
        when (plannerFilter) {
            PlannerFilter.ALL, PlannerFilter.VACATIONS -> absences
            PlannerFilter.SHIFTS -> emptyList()
        }
    }

    LaunchedEffect(workplace.id, rangeFrom, rangeTo) {
        loading = true
        val res = slotsRepo.getSlotsForRange(workplace.id, rangeFrom, rangeTo)
        if (res.isSuccess) {
            slots = res.getOrThrow()
            error = null
        } else {
            error = "Kunde inte ladda pass."
        }
        val staffRes = staffRepo.getStaff(workplace.id)
        if (staffRes.isSuccess) {
            staff = staffRes.getOrThrow()
        }
        absenceRepo.getAbsences(workplace.id).onSuccess { absences = it }
        loading = false
    }

    fun refresh() {
        schemaScope.launch {
            slotsRepo.getSlotsForRange(workplace.id, rangeFrom, rangeTo)
                .onSuccess { newSlots ->
                    slots = newSlots
                    val currentModal = modalState
                    when (currentModal) {
                        is ModalState.Overflow -> {
                            val updatedModalSlots = newSlots.filter { newSlot -> currentModal.slots.any { it.id == newSlot.id } }
                            modalState = if (updatedModalSlots.isEmpty()) null else ModalState.Overflow(updatedModalSlots)
                        }
                        is ModalState.DayList -> {
                            val updatedModalSlots = newSlots.filter { it.day == currentModal.day }
                            modalState = ModalState.DayList(currentModal.day, updatedModalSlots)
                        }
                        is ModalState.Edit -> {
                            val updatedSlot = newSlots.find { it.id == currentModal.slot.id }
                            modalState = if (updatedSlot != null) ModalState.Edit(updatedSlot) else null
                        }
                        else -> {}
                    }
                }
            absenceRepo.getAbsences(workplace.id)
                .onSuccess { absences = it }
        }
    }

    LaunchedEffect(createAbsenceTrigger) {
        if (createAbsenceTrigger > 0) {
            modalState = ModalState.CreateAbsence
        }
    }

    Div({
        style {
            property("display", "flex"); property("flex-direction", "column")
            property("gap", if (isMobile) "1rem" else "1.25rem")
            property("min-width", "0")
        }
    }) {
        SchemaToolbar(
            viewMode = viewMode,
            onViewModeChange = { viewMode = it },
            anchor = anchor,
            onAnchorChange = { anchor = it },
            isMobile = isMobile,
            isManager = isManager,
            filter = plannerFilter,
            onFilterChange = { plannerFilter = it },
            onCopyPrevWeek = if (isManager && viewMode == ViewMode.WEEK) {
                {
                    schemaScope.launch {
                        val targetMonday = mondayOf(anchor)
                        val prevMonday = addDays(targetMonday, -7)
                        val prevSunday = addDays(prevMonday, 6)
                        slotsRepo.getSlotsForRange(workplace.id, prevMonday, prevSunday).onSuccess { prevSlots ->
                            if (prevSlots.isEmpty()) {
                                copyFeedbackMsg = "Inga pass hittades i v.${weekNumber(prevMonday)}."
                            } else {
                                var count = 0
                                prevSlots.forEach { s ->
                                    val newDay = addDays(s.day, 7)
                                    slotsRepo.createSlot(workplace.id, CreateSlotRequest(
                                        day = newDay,
                                        startTime = s.startTime.take(5),
                                        endTime = s.endTime.take(5),
                                        assignedToUserId = s.assignedToUserId
                                    ))
                                    count++
                                }
                                copyFeedbackMsg = "Kopierade $count pass från v.${weekNumber(prevMonday)}!"
                                refresh()
                            }
                        }.onFailure {
                            copyFeedbackMsg = "Kunde inte hämta föregående veckas pass."
                        }
                    }
                }
            } else null
        )

        copyFeedbackMsg?.let { msg ->
            Div({ style {
                property("background", if (msg.startsWith("Kopierade")) "var(--rp-ok-bg)" else "var(--rp-warn-bg)")
                property("color", if (msg.startsWith("Kopierade")) "var(--rp-ok-text)" else "var(--rp-warn-text)")
                property("padding", "0.75rem 1.1rem"); property("border-radius", "14px")
                property("font-weight", "600"); property("font-size", "0.875rem")
                property("border", "1px solid " + if (msg.startsWith("Kopierade")) "var(--rp-ok-border)" else "var(--rp-warn-border)")
            } }) { Text(msg) }
        }

        error?.let { err ->
            Div({ style {
                property("background", "var(--rp-err-bg)"); property("color", "var(--rp-love)")
                property("padding", "0.85rem 1.1rem"); property("border-radius", "12px")
                property("font-weight", "600"); property("font-size", "0.9rem")
                property("border", "1px solid var(--rp-err-border)")
            } }) { Text(err) }
        }

        Div({ style {
            property("width", "100%")
            property("position", "relative")
            property("min-width", "0")
        } }) {
            if (loading) {
                Div({ style {
                    property("display", "flex"); property("align-items", "center"); property("justify-content", "center")
                    property("padding", "4rem 2rem"); property("gap", "0.85rem")
                } }) {
                    Div({ style {
                        property("width", "32px"); property("height", "32px")
                        property("border", "3px solid var(--rp-info-bg)"); property("border-top-color", "var(--rp-pine)")
                        property("border-radius", "50%"); property("animation", "spin 0.8s linear infinite")
                    } }) {}
                    Span({ style { property("color", "var(--rp-muted)"); property("font-weight", "500") } }) { Text("Laddar pass…") }
                }
            } else {
                if (viewMode == ViewMode.YEAR) {
                    YearView(
                        year = anchor.take(4).toIntOrNull() ?: 2026,
                        slots = filteredSlots,
                        isMobile = isMobile,
                        isNarrow = isNarrow,
                        onDayClick = { day ->
                            modalState = ModalState.DayList(day, slots.filter { it.day == day })
                        },
                        onWeekClick = { weekMonday ->
                            anchor = weekMonday
                            viewMode = ViewMode.WEEK
                        },
                        absences = filteredAbsences
                    )
                } else if (viewMode == ViewMode.MONTH) {
                    MonthView(
                        anchor = anchor,
                        slots = filteredSlots,
                        isManager = isManager,
                        currentUserId = currentUserId,
                        isMobile = isMobile,
                        staff = staff,
                        onDayClick = { day ->
                            if (isManager) {
                                modalState = ModalState.DayList(day, slots.filter { it.day == day })
                            }
                        },
                        onEdit = { slot ->
                            if (isManager) modalState = ModalState.Edit(slot)
                        },
                        onShowDay = { day, daySlots ->
                            modalState = ModalState.DayList(day, daySlots)
                        },
                        absences = filteredAbsences
                    )
                } else {
                    if (isManager) {
                        val slotsWithApplicants = slots.filter { it.assignedToUserId == null && it.interestedUsers.isNotEmpty() }
                        if (slotsWithApplicants.isNotEmpty()) {
                            ApplicantNoticeBanner(
                                slotsWithApplicants = slotsWithApplicants,
                                onReviewClick = { modalState = ModalState.Overflow(slotsWithApplicants) }
                            )
                        }
                    }

                    if (isMobile) {
                        Div({
                            style {
                                property("display", "flex")
                                property("justify-content", "flex-end")
                                property("margin-bottom", "0.5rem")
                                property("gap", "0.5rem")
                            }
                        }) {
                            Button({
                                attr("data-testid", "toggle-schema-view")
                                onClick { mobileViewGantt = !mobileViewGantt }
                                style {
                                    property("background", "var(--rp-surface)")
                                    property("color", "var(--rp-pine)")
                                    property("border", "1px solid var(--rp-hl-med)")
                                    property("border-radius", "50px")
                                    property("padding", "0.35rem 0.85rem")
                                    property("font-size", "0.8rem")
                                    property("font-weight", "700")
                                    property("cursor", "pointer")
                                }
                            }) {
                                Text(if (mobileViewGantt) "Byt till listvy" else "Byt till översikt")
                            }
                        }
                    }

                    if (isMobile && !mobileViewGantt) {
                        MobileAgendaView(
                            startDate = rangeFrom,
                            endDate = rangeTo,
                            staff = staff,
                            slots = filteredSlots,
                            absences = filteredAbsences,
                            isManager = isManager,
                            currentUserId = currentUserId,
                            onSlotClick = { slot ->
                                if (isManager) {
                                    if (slot.interestedUsers.isNotEmpty() && slot.assignedToUserId == null) {
                                        modalState = ModalState.Overflow(listOf(slot))
                                    } else {
                                        modalState = ModalState.Edit(slot)
                                    }
                                } else {
                                    if (slot.assignedToUserId == null) {
                                        val hasClaimed = currentUserId != null && slot.interestedUsers.any { it.id == currentUserId }
                                        confirmPending = PendingClaim(slot.id, slot.day, slot.startTime, slot.endTime, hasClaimed)
                                    }
                                }
                            },
                            onAbsenceClick = { abs -> selectedAbsence = abs },
                            onCreateSlot = { day, startMin, endMin, staffId ->
                                modalState = ModalState.Create(day, minutesToTime(startMin), minutesToTime(endMin), staffId)
                            },
                            onClaimSlot = { slotId ->
                                val s = slots.find { it.id == slotId }
                                if (s != null) confirmPending = PendingClaim(slotId, s.day, s.startTime, s.endTime, false)
                            },
                            onUnclaimSlot = { slotId ->
                                val s = slots.find { it.id == slotId }
                                if (s != null) confirmPending = PendingClaim(slotId, s.day, s.startTime, s.endTime, true)
                            }
                        )
                    } else {
                        UnifiedGanttPlanner(
                            startDate = rangeFrom,
                            endDate = rangeTo,
                            staff = staff,
                            slots = filteredSlots,
                            absences = filteredAbsences,
                            isManager = isManager,
                            currentUserId = currentUserId,
                            onSlotClick = { slot ->
                                if (isManager) {
                                    if (slot.interestedUsers.isNotEmpty() && slot.assignedToUserId == null) {
                                        modalState = ModalState.Overflow(listOf(slot))
                                    } else {
                                        modalState = ModalState.Edit(slot)
                                    }
                                } else {
                                    if (slot.assignedToUserId == null) {
                                        val hasClaimed = currentUserId != null && slot.interestedUsers.any { it.id == currentUserId }
                                        confirmPending = PendingClaim(slot.id, slot.day, slot.startTime, slot.endTime, hasClaimed)
                                    }
                                }
                            },
                            onAbsenceClick = { abs -> selectedAbsence = abs },
                            onCreateSlot = { day, startMin, endMin, staffId ->
                                modalState = ModalState.Create(day, minutesToTime(startMin), minutesToTime(endMin), staffId)
                            },
                            onUpdateSlotTime = { slotId, newDay, newStart, newEnd, newStaffId ->
                                schemaScope.launch {
                                    slotsRepo.updateSlot(slotId, se.mittpass.shared.api.UpdateSlotRequest(
                                        day = newDay,
                                        startTime = newStart,
                                        endTime = newEnd,
                                        assignedToUserId = newStaffId
                                    )).onSuccess { refresh() }
                                }
                            },
                            onDayUnassignedClick = { dayStr, daySlots ->
                                modalState = ModalState.DayList(dayStr, daySlots)
                            },
                            onClaimSlot = { slotId ->
                                val s = slots.find { it.id == slotId }
                                if (s != null) confirmPending = PendingClaim(slotId, s.day, s.startTime, s.endTime, false)
                            },
                            onUnclaimSlot = { slotId ->
                                val s = slots.find { it.id == slotId }
                                if (s != null) confirmPending = PendingClaim(slotId, s.day, s.startTime, s.endTime, true)
                            }
                        )
                    }
                }
            }
        }
    }

    modalState?.let { state ->
        when (state) {
            is ModalState.Create -> SlotModal(
                title = "Lägg till pass",
                subtitle = "Fyll i tid för passet den ${state.day}",
                day = state.day,
                startTime = state.startTime,
                endTime = state.endTime,
                allowDayEdit = true,
                confirmLabel = "Lägg till",
                staff = staff,
                absences = absences,
                initialAssignedUserId = state.assignedUserId,
                onConfirm = { d, s, e, breakMin, notify, assignedUserId ->
                    schemaScope.launch {
                        val req = se.mittpass.shared.api.CreateSlotRequest(
                            day = d,
                            startTime = s,
                            endTime = e,
                            notifyStaff = notify,
                            assignedToUserId = assignedUserId,
                            breakMinutes = breakMin
                        )
                        slotsRepo.createSlot(workplace.id, req)
                        modalState = null
                        refresh()
                    }
                },
                onCancel = { modalState = null }
            )
            is ModalState.Edit -> SlotModal(
                title = "Redigera pass",
                subtitle = "Ändra tider för det valda passet",
                day = state.slot.day,
                startTime = state.slot.startTime,
                endTime = state.slot.endTime,
                allowDayEdit = false,
                confirmLabel = "Spara ändringar",
                showDelete = true,
                staff = staff,
                absences = absences,
                initialAssignedUserId = state.slot.assignedToUserId,
                initialBreakMinutes = state.slot.breakMinutes,
                onConfirm = { d, s, e, breakMin, notify, assignedUserId ->
                    schemaScope.launch {
                        val req = se.mittpass.shared.api.UpdateSlotRequest(
                            day = d,
                            startTime = s,
                            endTime = e,
                            notifyStaff = notify,
                            assignedToUserId = assignedUserId,
                            breakMinutes = breakMin
                        )
                        slotsRepo.updateSlot(state.slot.id, req)
                        modalState = null
                        refresh()
                    }
                },
                onDelete = {
                    schemaScope.launch {
                        slotsRepo.deleteSlot(state.slot.id)
                        modalState = null
                        refresh()
                    }
                },
                onCancel = { modalState = null }
            )
            is ModalState.Overflow -> OverflowModal(
                slots = state.slots,
                isManager = isManager,
                currentUserId = currentUserId,
                onClaim = { id ->
                    val s = slots.find { it.id == id }
                    if (s != null) { modalState = null; confirmPending = PendingClaim(id, s.day, s.startTime, s.endTime, false) }
                },
                onUnclaim = { id ->
                    val s = slots.find { it.id == id }
                    if (s != null) { modalState = null; confirmPending = PendingClaim(id, s.day, s.startTime, s.endTime, true) }
                },
                onAssign = if (isManager) { { slotId, userId ->
                    schemaScope.launch {
                        slotsRepo.assignSlot(slotId, se.mittpass.shared.api.AssignSlotRequest(userId))
                        refresh()
                    }
                } } else null,
                onEdit = if (isManager) { s -> modalState = ModalState.Edit(s) } else null,
                onClose = { modalState = null },
                staff = staff
            )
            is ModalState.DayList -> OverflowModal(
                slots = state.slots,
                isManager = isManager,
                currentUserId = currentUserId,
                title = formatNiceDate(state.day),
                onClaim = { id ->
                    val s = slots.find { it.id == id }
                    if (s != null) { modalState = null; confirmPending = PendingClaim(id, s.day, s.startTime, s.endTime, false) }
                },
                onUnclaim = { id ->
                    val s = slots.find { it.id == id }
                    if (s != null) { modalState = null; confirmPending = PendingClaim(id, s.day, s.startTime, s.endTime, true) }
                },
                onAssign = if (isManager) { { slotId, userId ->
                    schemaScope.launch {
                        slotsRepo.assignSlot(slotId, se.mittpass.shared.api.AssignSlotRequest(userId))
                        refresh()
                    }
                } } else null,
                onEdit = if (isManager) { s -> modalState = ModalState.Edit(s) } else null,
                onClose = { modalState = null },
                extraAction = if (isManager) {
                    {
                        modalState = ModalState.Create(state.day, "09:00", "12:00")
                    }
                } else null,
                extraActionLabel = "+ Lägg till pass",
                staff = staff
            )
            is ModalState.CreateAbsence -> CreateAbsenceModal(
                staff = staff,
                currentUserId = currentUserId,
                isManager = isManager,
                onConfirm = { type, startDate, endDate, note, userId, status ->
                    schemaScope.launch {
                        absenceRepo.createAbsence(workplace.id, se.mittpass.shared.api.CreateAbsenceRequest(
                            type = type,
                            startDate = startDate,
                            endDate = endDate,
                            note = note,
                            userId = userId ?: currentUserId ?: 0L,
                            status = status
                        )).onSuccess {
                            modalState = null
                            copyFeedbackMsg = "Frånvaro / semester skapad!"
                            refresh()
                        }.onFailure {
                            error = "Kunde inte skapa frånvaro."
                        }
                    }
                },
                onCancel = { modalState = null }
            )
        }
    }

    confirmPending?.let { pending ->
        ConfirmClaimDialog(
            isUnclaim = pending.isUnclaim,
            day = pending.day,
            startTime = pending.startTime,
            endTime = pending.endTime,
            onConfirm = {
                confirmPending = null
                schemaScope.launch {
                    if (pending.isUnclaim) claimsRepo.unclaimSlot(pending.slotId)
                    else claimsRepo.claimSlot(pending.slotId)
                    refresh()
                }
            },
            onCancel = { confirmPending = null }
        )
    }



    selectedAbsence?.let { abs ->
        val staffMember = staff.find { it.userId == abs.userId }
        val staffName = staffMember?.let { "${it.firstName ?: ""} ${it.lastName ?: ""}".ifEmpty { it.email } } ?: "Anställd"

        AbsenceModal(
            absence = abs,
            staffMemberName = staffName,
            isManager = isManager,
            onApprove = {
                schemaScope.launch {
                    absenceRepo.reviewAbsence(abs.id, se.mittpass.shared.api.ReviewAbsenceRequest("approved")).onSuccess {
                        selectedAbsence = null
                        refresh()
                    }
                }
            },
            onReject = {
                schemaScope.launch {
                    absenceRepo.reviewAbsence(abs.id, se.mittpass.shared.api.ReviewAbsenceRequest("rejected")).onSuccess {
                        selectedAbsence = null
                        refresh()
                    }
                }
            },
            onDelete = {
                schemaScope.launch {
                    absenceRepo.deleteAbsence(abs.id).onSuccess {
                        selectedAbsence = null
                        refresh()
                    }
                }
            },
            onClose = { selectedAbsence = null }
        )
    }
}

private sealed class ModalState {
    data class Create(val day: String, val startTime: String, val endTime: String, val assignedUserId: Long? = null) : ModalState()
    data class Edit(val slot: SlotDto) : ModalState()
    data class Overflow(val slots: List<SlotDto>) : ModalState()
    data class DayList(val day: String, val slots: List<SlotDto>) : ModalState()
    object CreateAbsence : ModalState()
}
